/**
 * Che tipo di fatto e' stato registrato. Sono valori di contratto — finiscono nel testo che il Motore
 * archivia e nell'archivio del browser — quindi stanno in inglese minuscolo.
 */
export type TechnicalLogKind = 'action' | 'navigation' | 'network' | 'error';

/**
 * I dati che qualificano un fatto: stato di una risposta, durata, indirizzo della pagina. Valori semplici
 * e basta — una struttura annidata qui renderebbe il diario dipendente dalla forma di chi l'ha scritto,
 * e chi lo legge a valle si aspetta campi piatti su cui contare.
 */
export type TechnicalLogDetail = Readonly<Record<string, string | number | boolean>>;

/**
 * Una riga del diario tecnico del browser: cosa e' successo, quando, e i pochi dati che servono a
 * capirlo. E' deliberatamente povera — nessun corpo di richiesta, nessun testo scritto da chi usa il
 * portale, nessun indirizzo di posta — perche' finisce dentro una segnalazione che viaggia fuori di qui.
 */
export interface TechnicalLogEntry {
  /** Quando, in millisecondi dall'epoca: e' anche l'ordine e la chiave su cui l'archivio indicizza. */
  readonly at: number;
  readonly kind: TechnicalLogKind;
  /** Cosa e' successo, in una riga gia' leggibile. */
  readonly message: string;
  /** I dati che qualificano il fatto, a campi — vedi {@link TechnicalLogDetail}. */
  readonly detail?: TechnicalLogDetail;
}

/** Una riga da registrare: il momento lo mette chi registra, non chi chiama. */
export type TechnicalLogRecord = Omit<TechnicalLogEntry, 'at'>;

/**
 * Se una riga merita l'archivio: un errore, o una chiamata di rete andata storta (`failed`). Il resto —
 * gesti, navigazioni, chiamate riuscite — non entra: il diario serve a spiegare un guasto, e una chiamata
 * riuscita non ne spiega nessuno mentre occupa il budget della segnalazione e traccia la navigazione di
 * chi usa il portale.
 */
export function isTechnicalLogFailure(record: Pick<TechnicalLogEntry, 'kind' | 'detail'>): boolean {
  if (record.kind === 'error') return true;

  return record.kind === 'network' && record.detail?.['failed'] === true;
}

/**
 * Quanto indietro guarda una segnalazione. Trenta minuti sono la finestra decisa col Motore: prima di
 * quella un fatto non spiega piu' un guasto che si sta segnalando adesso.
 */
export const TECHNICAL_LOG_REPORT_WINDOW_MS = 30 * 60 * 1000;

/**
 * Quanto vive una riga nell'archivio del browser: quanto la finestra di una segnalazione. Oltre la
 * mezz'ora una riga non finisce in nessuna segnalazione, e resterebbe soltanto come traccia su un
 * computer che puo' essere di qualcun altro.
 */
export const TECHNICAL_LOG_RETENTION_MS = TECHNICAL_LOG_REPORT_WINDOW_MS;

/**
 * Quanti caratteri di diario una segnalazione puo' portare. Non e' una misura scelta qui: il Motore la
 * archivia in Table Storage, dove una proprieta' stringa si ferma a 32.768 caratteri, e il BFF taglia
 * comunque il di piu' (`SessionReport.MaxTechnicalLogsLength`). Tagliare gia' qui fa arrivare la parte
 * utile intera invece di un troncamento deciso da chi non sa dove finisce una riga.
 */
export const TECHNICAL_LOG_MAX_LENGTH = 30000;
